import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Save, Sparkles, Briefcase, AlertCircle, Camera } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfileCompletionModal = ({ user, onComplete, isOpen: externalIsOpen, onClose, isUpdate = false }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalIsOpen !== undefined ? externalIsOpen : internalOpen;
  const setOpen = (value) => {
    if (externalIsOpen !== undefined && onClose) {
      if (!value) onClose();
    } else {
      setInternalOpen(value);
    }
  };
  
  // Track initial values for update mode
  const [initialData, setInitialData] = useState(null);
  
  const [formData, setFormData] = useState({
    interests: user.interests || [],
    career_industries: user.career_industries || [],
    favorite_sport_team: user.favorite_sport_team || '',
    favorite_food: user.favorite_food || '',
    favorite_show: user.favorite_show || '',
    school: user.school || '',
    summer_goals: user.summer_goals || [],
    name: user.name || '',
    secondary_email: user.secondary_email || '',
    social_tiktok: user.social_tiktok || '',
    social_instagram: user.social_instagram || '',
    social_twitter: user.social_twitter || '',
    social_linkedin: user.social_linkedin || ''
  });
  const [newInterest, setNewInterest] = useState('');
  const [newGoal, setNewGoal] = useState('');
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(user.profile_picture || null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5MB');
      return;
    }
    setUploadingPhoto(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        let width = img.width, height = img.height;
        const maxDim = 400;
        if (width > maxDim || height > maxDim) {
          if (width > height) { height = Math.round((height * maxDim) / width); width = maxDim; }
          else { width = Math.round((width * maxDim) / height); height = maxDim; }
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        const resized = canvas.toDataURL('image/jpeg', 0.8);
        setPhotoPreview(resized);
        // Save immediately via PATCH
        try {
          const res = await fetch(`${API_URL}/api/users/me`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ profile_picture: resized })
          });
          if (res.ok) toast.success('Photo uploaded!');
          else toast.error('Failed to upload photo');
        } catch { toast.error('Upload error'); }
        setUploadingPhoto(false);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const popularInterests = [
    'Basketball', 'Music', 'Gaming', 'Art', 'Fashion',
    'Cooking', 'Soccer', 'Dance', 'Photography', 'Technology',
    'Reading', 'Fitness', 'Movies', 'Social Media'
  ];

  const careerIndustries = [
    'Healthcare', 'Technology/IT', 'Business/Finance', 'Education',
    'Arts & Entertainment', 'Public Service/Government', 'Construction/Trades',
    'Retail/Customer Service', 'Food Service/Hospitality', 'Transportation/Logistics',
    'Sports/Recreation', 'Media/Communications', 'Science/Research', 'Law/Legal',
    'Manufacturing', 'Non-Profit/Social Services'
  ];

  useEffect(() => {
    // Store initial data for comparison in update mode
    if (open && !initialData) {
      setInitialData({
        interests: [...(user.interests || [])],
        career_industries: [...(user.career_industries || [])],
        favorite_sport_team: user.favorite_sport_team || '',
        favorite_food: user.favorite_food || '',
        favorite_show: user.favorite_show || '',
        school: user.school || '',
        summer_goals: [...(user.summer_goals || [])],
        name: user.name || '',
        secondary_email: user.secondary_email || '',
        social_tiktok: user.social_tiktok || '',
        social_instagram: user.social_instagram || '',
        social_twitter: user.social_twitter || '',
        social_linkedin: user.social_linkedin || ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, initialData]);

  useEffect(() => {
    // Auto-open logic for first-time profile completion
    // Don't show if user needs to change password first
    if (externalIsOpen === undefined && user.role === 'youth' && !user.needs_password_change) {
      // Check if this is the 14-day update reminder
      const lastProfileUpdate = localStorage.getItem(`profileUpdate_${user.id}`);
      const firstLogin = user.first_login_at || user.created_at;
      
      if (firstLogin) {
        const firstLoginDate = new Date(firstLogin);
        const now = new Date();
        const daysSinceFirstLogin = Math.floor((now - firstLoginDate) / (1000 * 60 * 60 * 24));
        
        // Check if 14 days have passed and update hasn't been done yet
        const updateDueDate = new Date(firstLoginDate);
        updateDueDate.setDate(updateDueDate.getDate() + 14);
        
        if (daysSinceFirstLogin >= 14 && !lastProfileUpdate) {
          // Show update reminder
          const hasShownUpdateModal = sessionStorage.getItem('profileUpdateModalShown');
          if (!hasShownUpdateModal) {
            setTimeout(() => {
              setInternalOpen(true);
              sessionStorage.setItem('profileUpdateModalShown', 'true');
            }, 2000);
          }
          return;
        }
      }

      // Show modal if profile is not complete (first-time) — CANNOT be dismissed
      if (!user.profile_completed) {
        setInternalOpen(true);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, externalIsOpen]);

  const addInterest = (interest) => {
    if (interest.trim() && !formData.interests.includes(interest)) {
      setFormData({ ...formData, interests: [...formData.interests, interest.trim()] });
    }
  };

  const removeInterest = (interest) => {
    setFormData({
      ...formData,
      interests: formData.interests.filter(i => i !== interest)
    });
  };

  const toggleCareerIndustry = (industry) => {
    if (formData.career_industries.includes(industry)) {
      setFormData({
        ...formData,
        career_industries: formData.career_industries.filter(i => i !== industry)
      });
    } else if (formData.career_industries.length < 3) {
      setFormData({
        ...formData,
        career_industries: [...formData.career_industries, industry]
      });
    } else {
      toast.error('You can select up to 3 career industries');
    }
  };

  const addGoal = () => {
    if (newGoal.trim() && !formData.summer_goals.includes(newGoal)) {
      setFormData({ ...formData, summer_goals: [...formData.summer_goals, newGoal.trim()] });
      setNewGoal('');
    }
  };

  const removeGoal = (goal) => {
    setFormData({
      ...formData,
      summer_goals: formData.summer_goals.filter(g => g !== goal)
    });
  };

  // Check if user has added at least one new thing (for update mode)
  const hasAddedNewContent = () => {
    if (!initialData) return true;
    
    // Check for new interests
    const newInterests = formData.interests.filter(i => !initialData.interests.includes(i));
    if (newInterests.length > 0) return true;
    
    // Check for new career industries
    const newCareers = formData.career_industries.filter(i => !initialData.career_industries.includes(i));
    if (newCareers.length > 0) return true;
    
    // Check for new goals
    const newGoals = formData.summer_goals.filter(g => !initialData.summer_goals.includes(g));
    if (newGoals.length > 0) return true;
    
    // Check if any text fields were updated
    if (formData.favorite_sport_team !== initialData.favorite_sport_team && formData.favorite_sport_team) return true;
    if (formData.favorite_food !== initialData.favorite_food && formData.favorite_food) return true;
    if (formData.favorite_show !== initialData.favorite_show && formData.favorite_show) return true;
    if (formData.school !== initialData.school && formData.school) return true;
    if (formData.name !== initialData.name && formData.name) return true;
    if (formData.secondary_email !== initialData.secondary_email && formData.secondary_email) return true;
    if (formData.social_tiktok !== initialData.social_tiktok && formData.social_tiktok) return true;
    if (formData.social_instagram !== initialData.social_instagram && formData.social_instagram) return true;
    if (formData.social_twitter !== initialData.social_twitter && formData.social_twitter) return true;
    if (formData.social_linkedin !== initialData.social_linkedin && formData.social_linkedin) return true;
    
    return false;
  };

  const handleSave = async () => {
    // Validation for first-time profile completion
    if (!isUpdate) {
      if (formData.interests.length < 2) {
        toast.error('Please select at least 2 interests');
        return;
      }
      
      if (formData.career_industries.length < 2) {
        toast.error('Please select at least 2 career industries');
        return;
      }
      
      if (!formData.school.trim()) {
        toast.error('Please enter your school name');
        return;
      }
      
      if (!formData.favorite_food.trim()) {
        toast.error('Please enter your favorite food');
        return;
      }

      // NEW: Require at least 1 summer goal
      if (formData.summer_goals.length < 1) {
        toast.error('Please add at least 1 summer goal');
        return;
      }
    } else {
      // Validation for update mode - must add at least one new thing
      if (!hasAddedNewContent()) {
        toast.error('Please add at least one new item to your profile');
        return;
      }
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/users/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...formData,
          profile_completed: true,
          last_profile_update: new Date().toISOString()
        })
      });

      if (response.ok) {
        const updatedUser = await response.json();
        toast.success(isUpdate ? 'Profile updated! Thanks for sharing more about yourself! 🎉' : 'Profile completed! 🎉');
        
        // Mark the 14-day update as complete
        if (isUpdate) {
          localStorage.setItem(`profileUpdate_${user.id}`, new Date().toISOString());
        }
        
        setOpen(false);
        if (onComplete) onComplete(updatedUser);
      } else {
        toast.error('Failed to update profile');
      }
    } catch (error) {
      toast.error('Connection error');
    }
    setLoading(false);
  };

  const completionPercentage = () => {
    const fields = [
      formData.interests.length >= 2,
      formData.career_industries.length >= 2,
      formData.school,
      formData.favorite_food,
      formData.summer_goals.length >= 1  // Added summer goals requirement
    ];
    const completed = fields.filter(Boolean).length;
    return Math.round((completed / fields.length) * 100);
  };

  // Check for 14-day update mode
  const is14DayUpdate = () => {
    const firstLogin = user.first_login_at || user.created_at;
    if (!firstLogin) return false;
    
    const firstLoginDate = new Date(firstLogin);
    const now = new Date();
    const daysSinceFirstLogin = Math.floor((now - firstLoginDate) / (1000 * 60 * 60 * 24));
    const lastUpdate = localStorage.getItem(`profileUpdate_${user.id}`);
    
    return daysSinceFirstLogin >= 14 && !lastUpdate && user.profile_completed;
  };

  const modalTitle = is14DayUpdate() ? 'Update Your Profile' : 'Complete Your Profile';
  const modalDescription = is14DayUpdate() 
    ? "It's been 2 weeks! Tell us more about yourself - add at least one new thing."
    : 'Help us personalize your experience (required)';

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[85vh] overflow-y-auto" hideClose onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-primary" />
            {modalTitle}
          </DialogTitle>
          <p className="text-muted-foreground">{modalDescription}</p>
        </DialogHeader>

        {/* Required notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            {is14DayUpdate() 
              ? 'Please add at least one new interest, career, goal, or update any field to continue.'
              : 'This step is required. Please fill in at least 2 interests, 2 career industries, your school, and favorite food.'}
          </p>
        </div>

        {/* Profile Photo Upload */}
        <div className="flex flex-col items-center mb-2">
          <input type="file" ref={photoInputRef} onChange={handlePhotoSelect} accept="image/*" className="hidden" data-testid="onboarding-photo-input" />
          <div className="relative cursor-pointer group" onClick={() => photoInputRef.current?.click()}>
            <Avatar className="w-20 h-20 ring-4 ring-primary/20">
              {photoPreview ? <AvatarImage src={photoPreview} alt="Profile" /> : null}
              <AvatarFallback className="bg-primary/10 text-2xl font-bold text-primary">{user.name?.charAt(0) || '?'}</AvatarFallback>
            </Avatar>
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="w-6 h-6 text-white" />
            </div>
            {uploadingPhoto && <div className="absolute inset-0 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}
          </div>
          <button onClick={() => photoInputRef.current?.click()} className="text-xs text-primary font-medium mt-2 hover:underline" data-testid="upload-onboarding-photo-btn">
            {photoPreview ? 'Change Photo' : 'Add Profile Photo'}
          </button>
        </div>

        {/* Progress Bar */}
        {!is14DayUpdate() && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">Profile Completion</span>
              <span className="text-sm font-bold text-primary">{completionPercentage()}%</span>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${completionPercentage()}%` }}
                className="h-full bg-gradient-to-r from-primary to-secondary"
              />
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Name & Secondary Email - Only show if editing existing profile */}
          {(isUpdate || externalIsOpen) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div data-testid="name-section">
                <Label htmlFor="name" className="text-base font-bold mb-2 block">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className="rounded-xl h-12"
                />
              </div>
              <div data-testid="secondary-email-section">
                <Label htmlFor="secondary_email" className="text-base font-bold mb-2 block">
                  Secondary Email <span className="text-sm font-normal text-muted-foreground">(Optional)</span>
                </Label>
                <Input
                  id="secondary_email"
                  type="email"
                  value={formData.secondary_email}
                  onChange={(e) => setFormData({ ...formData, secondary_email: e.target.value })}
                  placeholder="backup@email.com"
                  className="rounded-xl h-12"
                />
              </div>
            </div>
          )}

          {/* Interests */}
          <div data-testid="interests-section">
            <Label className="text-base font-bold mb-3 block">
              What are you into? * <span className="text-sm font-normal text-muted-foreground">(Select at least 2)</span>
            </Label>
            <div className="flex flex-wrap gap-2 mb-3">
              {popularInterests.map((interest) => (
                <Badge
                  key={interest}
                  onClick={() => addInterest(interest)}
                  className={`cursor-pointer transition-all ${
                    formData.interests.includes(interest)
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-foreground hover:bg-gray-200'
                  }`}
                >
                  {interest}
                  {formData.interests.includes(interest) && ' ✓'}
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add custom interest..."
                value={newInterest}
                onChange={(e) => setNewInterest(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addInterest(newInterest);
                    setNewInterest('');
                  }
                }}
                className="rounded-xl"
              />
              <Button
                onClick={() => {
                  addInterest(newInterest);
                  setNewInterest('');
                }}
                size="icon"
                className="rounded-xl"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {formData.interests.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {formData.interests.map((interest) => (
                  <Badge key={interest} className="bg-primary text-white">
                    {interest}
                    <X
                      className="w-3 h-3 ml-1 cursor-pointer"
                      onClick={() => removeInterest(interest)}
                    />
                  </Badge>
                ))}
              </div>
            )}
            {formData.interests.length < 2 && (
              <p className="text-xs text-red-500 mt-1">Please select at least 2 interests ({formData.interests.length}/2)</p>
            )}
          </div>

          {/* Career Industries - MANDATORY */}
          <div data-testid="career-section">
            <Label className="text-base font-bold mb-2 block flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-500" />
              Career Interests * <span className="text-sm font-normal text-muted-foreground">(Select 2-3)</span>
            </Label>
            <p className="text-sm text-muted-foreground mb-3">What industries are you interested in for your future career?</p>
            <div className="flex flex-wrap gap-2">
              {careerIndustries.map((industry) => (
                <Badge
                  key={industry}
                  onClick={() => toggleCareerIndustry(industry)}
                  className={`cursor-pointer transition-all ${
                    formData.career_industries.includes(industry)
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-50 text-blue-800 hover:bg-blue-100'
                  }`}
                >
                  {industry}
                  {formData.career_industries.includes(industry) && ' ✓'}
                </Badge>
              ))}
            </div>
            {formData.career_industries.length > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                Selected: {formData.career_industries.join(', ')} ({formData.career_industries.length}/3)
              </p>
            )}
            {formData.career_industries.length < 2 && (
              <p className="text-xs text-red-500 mt-1">Please select at least 2 career industries ({formData.career_industries.length}/2)</p>
            )}
          </div>

          {/* School */}
          <div data-testid="school-section">
            <Label htmlFor="school" className="text-base font-bold mb-2 block">School *</Label>
            <Input
              id="school"
              value={formData.school}
              onChange={(e) => setFormData({ ...formData, school: e.target.value })}
              placeholder="What school do you attend?"
              className="rounded-xl h-12"
            />
            {!formData.school && (
              <p className="text-xs text-red-500 mt-1">Please enter your school</p>
            )}
          </div>

          {/* Favorites */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="sport" className="text-sm font-semibold mb-2 block">Favorite Team</Label>
              <Input
                id="sport"
                value={formData.favorite_sport_team}
                onChange={(e) => setFormData({ ...formData, favorite_sport_team: e.target.value })}
                placeholder="Lakers, Yankees..."
                className="rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="food" className="text-sm font-semibold mb-2 block">Favorite Food *</Label>
              <Input
                id="food"
                value={formData.favorite_food}
                onChange={(e) => setFormData({ ...formData, favorite_food: e.target.value })}
                placeholder="Pizza, Tacos..."
                className="rounded-xl"
              />
              {!formData.favorite_food && (
                <p className="text-xs text-red-500 mt-1">Required</p>
              )}
            </div>
            <div>
              <Label htmlFor="show" className="text-sm font-semibold mb-2 block">Favorite Show</Label>
              <Input
                id="show"
                value={formData.favorite_show}
                onChange={(e) => setFormData({ ...formData, favorite_show: e.target.value })}
                placeholder="Stranger Things..."
                className="rounded-xl"
              />
            </div>
          </div>

          {/* Summer Goals - REQUIRED */}
          <div data-testid="goals-section">

            {/* Social Media Handles */}
            <div className="mb-6">
              <Label className="text-base font-bold mb-3 block">
                Social Media <span className="text-sm font-normal text-muted-foreground">(Optional — show on your profile)</span>
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium w-20 flex-shrink-0">TikTok</span>
                  <Input
                    value={formData.social_tiktok}
                    onChange={(e) => setFormData({ ...formData, social_tiktok: e.target.value.replace('@', '') })}
                    placeholder="username"
                    className="rounded-xl"
                    data-testid="social-tiktok-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium w-20 flex-shrink-0">Instagram</span>
                  <Input
                    value={formData.social_instagram}
                    onChange={(e) => setFormData({ ...formData, social_instagram: e.target.value.replace('@', '') })}
                    placeholder="username"
                    className="rounded-xl"
                    data-testid="social-instagram-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium w-20 flex-shrink-0">X / Twitter</span>
                  <Input
                    value={formData.social_twitter}
                    onChange={(e) => setFormData({ ...formData, social_twitter: e.target.value.replace('@', '') })}
                    placeholder="username"
                    className="rounded-xl"
                    data-testid="social-twitter-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium w-20 flex-shrink-0">LinkedIn</span>
                  <Input
                    value={formData.social_linkedin}
                    onChange={(e) => setFormData({ ...formData, social_linkedin: e.target.value })}
                    placeholder="profile slug"
                    className="rounded-xl"
                    data-testid="social-linkedin-input"
                  />
                </div>
              </div>
            </div>

            <Label className="text-base font-bold mb-3 block">
              Summer Goals * <span className="text-sm font-normal text-muted-foreground">(Add at least 1)</span>
            </Label>
            <p className="text-sm text-muted-foreground mb-3">What do you want to achieve this summer? Share at least one goal!</p>
            <div className="flex gap-2 mb-3">
              <Input
                placeholder="e.g., Learn a new skill, Save $500..."
                value={newGoal}
                onChange={(e) => setNewGoal(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addGoal()}
                className="rounded-xl"
              />
              <Button onClick={addGoal} size="icon" className="rounded-xl">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {formData.summer_goals.length > 0 && (
              <div className="space-y-2">
                {formData.summer_goals.map((goal, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                    <span className="text-sm">{goal}</span>
                    <Button
                      onClick={() => removeGoal(goal)}
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {formData.summer_goals.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Please add at least 1 summer goal</p>
            )}
          </div>
        </div>

        <div className="mt-6">
          <Button
            data-testid="save-profile-button"
            onClick={handleSave}
            disabled={loading || (!is14DayUpdate() && completionPercentage() < 100)}
            className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-bold"
          >
            <Save className="w-5 h-5 mr-2" />
            {loading ? 'Saving...' : (is14DayUpdate() ? 'Update Profile' : 'Save & Continue')}
          </Button>
          {!is14DayUpdate() && completionPercentage() < 100 && (
            <p className="text-center text-xs text-muted-foreground mt-2">
              Complete all required fields to continue
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileCompletionModal;
